import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { FlightRoundVM, Paginated } from 'src/api/contracts';
import flightRoundApiService from 'src/api/services/flightRoundApiService';
import { AppState } from 'src/store/store';
import { FlightRound, FlightRoundPeriod } from './types';

const initialState: FlightRound = {
    status: 'initial',
    items: [],
    total: 0,
    period: null,
    pagination: {
        pageNumber: 1,
        pageSize: 100
    },
    selected: [],
    requestId: ''
};

export const loadSeatsQuotaCurrentPage = createAsyncThunk<Paginated<FlightRoundVM>, void, { state: AppState }>(
    'flight-round/load-current-page',
    async (_, { getState }) => {
        const state = getState();
        const period = selectFlightRoundPeriod(state);
        if (!period || !period.dateStart || !period.dateEnd) {
            throw new Error('Не задан период');
        }
        const pagination = selectFlightRoundPagination(state);
        const resp = await flightRoundApiService.loadFlightRound({
            filter: {
                dateStart: period.dateStart!.format('YYYY-MM-DD'),
                dateEnd: period.dateEnd!.format('YYYY-MM-DD')
            },
            pagination: {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize
            }
        });
        return resp;
    }
);

export const deleteFlightRound = createAsyncThunk<void, void, { state: AppState }>(
    'flight-round/delete',
    async (_, { getState }) => {
        const selected = selectFlightRoundSelection(getState());
        await flightRoundApiService.deleteFlightRound(selected);
    }
);

export const loadSeatsQuotaNextPage = createAsyncThunk<
    Paginated<FlightRoundVM>,
    { pageNumber: number; pageSize?: number },
    { state: AppState }
>('flight-round/load-next-page', async (nextPagination: { pageNumber: number; pageSize?: number }, { getState }) => {
    const state = getState();
    const period = selectFlightRoundPeriod(state);
    if (!period || !period.dateStart || !period.dateEnd) {
        throw new Error('Не задан период');
    }
    const pagination = selectFlightRoundPagination(state);
    const resp = await flightRoundApiService.loadFlightRound({
        filter: {
            dateStart: period.dateStart.format('YYYY-MM-DD'),
            dateEnd: period.dateEnd.format('YYYY-MM-DD')
        },
        pagination: {
            pageNumber: nextPagination.pageNumber,
            pageSize: nextPagination.pageSize ?? pagination.pageSize
        }
    });
    return resp;
});

const slice = createSlice({
    name: 'flight-round',
    initialState,
    reducers: {
        reset: () => initialState,
        periodChanged: (state, action: PayloadAction<FlightRoundPeriod>) => {
            state.period = action.payload;
            state.items = [];
            state.total = 0;
        },
        selectionChanged: (state, action: PayloadAction<number[]>) => {
            state.selected = action.payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(loadSeatsQuotaCurrentPage.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.selected = [];
            })
            .addCase(loadSeatsQuotaCurrentPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                }
            })
            .addCase(loadSeatsQuotaCurrentPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = [];
                    state.total = 0;
                }
            })
            .addCase(loadSeatsQuotaNextPage.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.pagination.pageNumber = action.meta.arg.pageNumber;
                state.selected = [];
                if (action.meta.arg.pageSize) {
                    state.pagination.pageSize = action.meta.arg.pageSize;
                }
            })
            .addCase(loadSeatsQuotaNextPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                    state.pagination.pageNumber = action.payload.pageNumber;
                    state.pagination.pageSize = action.payload.pageSize;
                }
            })
            .addCase(loadSeatsQuotaNextPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = [];
                }
            })
            .addCase(deleteFlightRound.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteFlightRound.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.selected = [];
                }
            })
            .addCase(deleteFlightRound.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                }
            });
    }
});

const selectFlightRound = (state: AppState) => state.flightRound;

export const selectFlightRoundStatus = (state: AppState) => selectFlightRound(state).status;

export const selectFlightRoundIsLoading = (state: AppState) => selectFlightRoundStatus(state) === 'loading';

export const selectFlightRoundItems = (state: AppState) => selectFlightRound(state).items;

export const selectFlightRoundTotal = (state: AppState) => selectFlightRound(state).total;

export const selectFlightRoundPeriod = (state: AppState) => selectFlightRound(state).period;

export const selectFlightRoundCanLoadData = createSelector(
    selectFlightRoundPeriod,
    (period) => period && period.dateStart && period.dateEnd
);

export const selectFlightRoundPagination = (state: AppState) => selectFlightRound(state).pagination;

export const selectFlightRoundSelection = (state: AppState) => selectFlightRound(state).selected;

export const { periodChanged, selectionChanged, reset } = slice.actions;

export default slice.reducer;
